import { useInfiniteQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { useInboxGrouping } from '@/hooks/inbox/useInboxGrouping';
import type { InboxGrouping, InboxPage } from '@/services/conversations/inbox';
import { getAllInquiriesV2QueryOptions, type InquiryV2 } from '@/services/getInquiriesV2';
import { userIdAtom } from '@/services/globalState';
import {
  getInquiryGroupExperts,
  getInquiryGroupKey,
  getInquiryGroupStateCounts,
  getUniqueInquiryLabels,
  type InquiryGroup,
  type InquiryGroupStateCounts,
} from '@/util/inquiryGroups';
import { getModRouteLabelsForGroup } from '@/utils/modHelpers';

const buildGroupInquiriesByKey = (inquiries: InquiryV2[]) => {
  const grouped = new Map<string, InquiryV2[]>();

  inquiries.forEach((inquiry) => {
    const key = getInquiryGroupKey(inquiry);

    const existing = grouped.get(key);

    if (existing) {
      existing.push(inquiry);

      return;
    }

    grouped.set(key, [inquiry]);
  });

  return grouped;
};

const buildGroupStateBreakdownByKey = (fullGroupInquiriesByKey: Map<string, InquiryV2[]>) => {
  const breakdownByKey = new Map<string, InquiryGroupStateCounts>();

  fullGroupInquiriesByKey.forEach((groupInquiries, key) => {
    const breakdown = getInquiryGroupStateCounts(groupInquiries);

    if (breakdown) {
      breakdownByKey.set(key, breakdown);
    }
  });

  return breakdownByKey;
};

/**
 * Whether the all-inquiries list is needed at all. When the server groups a tab by question, every
 * thread of a question, from either tab, arrives inside its group, so a row left over is a lone thread
 * with no sibling to enrich and the flat All list would be a second fetch of the same inbox. While a
 * grouped tab is still loading nothing is fetched either; if the server then answers flat (an old
 * server, a refused request) the tab groups on the client and the list is wanted after all.
 */
export function needsAllInquiries(
  grouping: InboxGrouping,
  pages: readonly (InboxPage | undefined)[] | undefined
) {
  if (grouping !== 'question') {
    return true;
  }

  if (!pages?.length) {
    return false;
  }

  return !pages.every((page) => page?.questionGroups);
}

/**
 * Enriches tab-scoped inquiry groups with full sibling data from the all-inquiries query.
 * This ensures grouped cards on Open/Closed tabs show all expert avatars and
 * open/closed state badges, even when some siblings belong to the other tab.
 */
export function useInquiryGroupEnrichment({
  enabled,
  cancerTypeId,
  isAnonymousInquiryGroupingEnabled,
  pages,
}: {
  enabled: boolean;
  cancerTypeId?: number;
  isAnonymousInquiryGroupingEnabled: boolean;
  /** The tab's own pages, which say whether the server grouped them. */
  pages?: readonly (InboxPage | undefined)[];
}) {
  const userId = useAtomValue(userIdAtom);

  const grouping = useInboxGrouping();

  const allInquiriesQuery = useInfiniteQuery({
    ...getAllInquiriesV2QueryOptions(userId, cancerTypeId),
    enabled: enabled && !!userId && needsAllInquiries(grouping, pages),
  });

  const allInquiriesList = useMemo<InquiryV2[]>(
    () => allInquiriesQuery.data?.pages?.flatMap((page) => page?.inquiries ?? []) ?? [],
    [allInquiriesQuery.data?.pages]
  );

  const fullGroupInquiriesByKey = useMemo(() => {
    if (!allInquiriesList.length) {
      return new Map<string, InquiryV2[]>();
    }

    return buildGroupInquiriesByKey(allInquiriesList);
  }, [allInquiriesList]);

  const groupStateBreakdownByKey = useMemo(
    () => buildGroupStateBreakdownByKey(fullGroupInquiriesByKey),
    [fullGroupInquiriesByKey]
  );

  return {
    getFullGroupInquiries: (group: InquiryGroup) =>
      fullGroupInquiriesByKey.get(group.groupKey) ?? group.inquiries,
    getGroupExperts: (group: InquiryGroup) =>
      getInquiryGroupExperts(
        { ...group, inquiries: fullGroupInquiriesByKey.get(group.groupKey) ?? group.inquiries },
        isAnonymousInquiryGroupingEnabled,
        userId ?? undefined
      ),
    getGroupStateBreakdown: (group: InquiryGroup) => groupStateBreakdownByKey.get(group.groupKey),
    getGroupLabels: (group: InquiryGroup) =>
      getUniqueInquiryLabels(fullGroupInquiriesByKey.get(group.groupKey) ?? group.inquiries),
    getGroupRouteLabels: (group: InquiryGroup) =>
      getModRouteLabelsForGroup(
        { ...group, inquiries: fullGroupInquiriesByKey.get(group.groupKey) ?? group.inquiries },
        userId ?? undefined
      ),
  };
}
